/**
 * Pictures for an email, hosted by us — 18 Sep 2026.
 *
 * The image block took a URL and nothing else, which meant every picture in
 * every newsletter lived on somebody else's server: a Dropbox share that
 * expires, a page that gets reorganised, a CDN nobody renews. An email is read
 * for years after it is sent, and a message whose images have gone is worse
 * than one that never had any.
 *
 * So they go in our own bucket, under `emailImages/`, which is **the one path
 * anybody may read**. That is not an oversight — an inbox fetches an image as
 * an anonymous stranger, with no session it could present — and the consequence
 * is that anything uploaded here is on the public internet. The id is random so
 * the path cannot be guessed from a campaign's name. See `storage.rules`.
 *
 * ## Downscaled on the way in
 *
 * A 4 000-pixel photograph straight off a camera is eight megabytes, and an
 * email is opened on a phone over mobile data. Everything is resized to 1 200px
 * on its longest edge — twice the 600px content column, so it stays sharp on a
 * retina screen — and re-encoded as JPEG unless it has transparency to keep.
 *
 * A GIF is passed through untouched. Re-encoding one through a canvas keeps the
 * first frame and throws the animation away, which is the one thing somebody
 * uploading a GIF wanted.
 *
 * ## Cropping happens here, not in the message
 *
 * `object-fit` is a no-op in Outlook and a table cell cannot clip what is
 * inside it, so a crop expressed in markup is a crop that works in half the
 * inboxes and stretches the picture in the other half. The only version that
 * holds everywhere is a file that is already the right shape.
 *
 * So a ratio is applied to the pixels and the result is what gets stored. The
 * upload keeps the original beside it so the ratio can be changed again without
 * asking for the file a second time — `srcOriginal` on the block.
 */
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage'
import { app } from '@/shared/firebase/client'

/** Twice the 600px column, which is what a retina screen asks for. */
const MAX_EDGE = 1200

/** The rule refuses more; this catches it before the upload rather than after. */
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024

const ALLOWED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']

/** The shapes on offer. `''` is the picture's own. */
export const RATIOS: { key: string; label: string }[] = [
  { key: '', label: 'Original' },
  { key: '16:9', label: '16:9' },
  { key: '3:2', label: '3:2' },
  { key: '4:3', label: '4:3' },
  { key: '1:1', label: 'Square' },
]

/** `16:9` → 1.777…, or null where there is nothing to crop to. */
function ratioOf(ratio: string): number | null {
  const m = /^(\d+):(\d+)$/.exec(ratio.trim())
  if (!m) return null
  const w = Number(m[1])
  const h = Number(m[2])
  return w > 0 && h > 0 ? w / h : null
}

/**
 * The middle of the picture, in the shape asked for.
 *
 * Centred rather than smart: a face-aware crop is a different project, and a
 * centre crop is what a writer expects when they pick 16:9 and can correct by
 * cropping the file themselves if it matters.
 */
function cropRect(w: number, h: number, want: number): { x: number; y: number; w: number; h: number } {
  const have = w / h
  if (Math.abs(have - want) < 0.001) return { x: 0, y: 0, w, h }
  if (have > want) {
    // Too wide: take a full-height slice out of the middle.
    const cw = Math.round(h * want)
    return { x: Math.round((w - cw) / 2), y: 0, w: cw, h }
  }
  const ch = Math.round(w / want)
  return { x: 0, y: Math.round((h - ch) / 2), w, h: ch }
}

/**
 * Smaller, the right shape, and in a format an inbox understands.
 *
 * Returns the input untouched where there is nothing to gain: a GIF, or an
 * uncropped picture already inside the box. Transparency survives as PNG;
 * everything else becomes JPEG, a third of the size at a quality nobody can see
 * the difference in on a phone.
 */
async function shrink(file: Blob, ratio: string): Promise<Blob> {
  // A GIF is left alone entirely — including its shape. Cropping one would mean
  // re-encoding it, which is the one thing that loses the animation.
  if (file.type === 'image/gif') return file

  const want = ratioOf(ratio)
  const bitmap = await createImageBitmap(file)
  const longest = Math.max(bitmap.width, bitmap.height)
  if (want === null && longest <= MAX_EDGE && file.size <= 600 * 1024) {
    bitmap.close()
    return file
  }

  const cut = want === null
    ? { x: 0, y: 0, w: bitmap.width, h: bitmap.height }
    : cropRect(bitmap.width, bitmap.height, want)
  const scale = Math.min(1, MAX_EDGE / Math.max(cut.w, cut.h))
  const w = Math.round(cut.w * scale)
  const h = Math.round(cut.h * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    bitmap.close()
    return file
  }
  // PNG and WebP may carry transparency, and a JPEG would fill it with black.
  const keepAlpha = file.type === 'image/png' || file.type === 'image/webp'
  if (!keepAlpha) {
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, w, h)
  }
  ctx.drawImage(bitmap, cut.x, cut.y, cut.w, cut.h, 0, 0, w, h)
  bitmap.close()

  const type = keepAlpha ? 'image/png' : 'image/jpeg'
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, type, 0.86),
  )
  // A canvas that refuses to encode is a browser problem, not a reason to lose
  // the upload — the original is still a picture. A crop is kept whatever it
  // weighs, because the shape is the point.
  if (!blob) return file
  return want !== null || blob.size < file.size ? blob : file
}

/**
 * Put a picture in the bucket and hand back the URL an inbox can fetch.
 *
 * The id is random rather than derived from the filename: two campaigns both
 * uploading `hero.jpg` must not overwrite each other, and a path that carried
 * the name would let anybody guess what is in a bucket they can read.
 */
/** The one place anything is written to the bucket. */
async function put(blob: Blob, fallbackType: string): Promise<string> {
  const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
  const path = `emailImages/${id}`
  await uploadBytes(ref(getStorage(app), path), blob, {
    contentType: blob.type || fallbackType,
    // A year, because an email is read long after it is sent and the bytes
    // never change — the id is new every time.
    cacheControl: 'public, max-age=31536000, immutable',
  })
  return getDownloadURL(ref(getStorage(app), path))
}

/**
 * Put a picture in the bucket and hand back what the block needs.
 *
 * `original` is always the uncropped version, kept so the ratio can be changed
 * later without asking for the file again. With no crop the two are the same
 * object and only one upload happens — the commonest case costs nothing extra.
 */
export async function uploadEmailImage(
  file: File,
  ratio = '',
): Promise<{ src: string; original: string }> {
  if (!ALLOWED.includes(file.type)) {
    throw new Error('Images only — PNG, JPEG, WebP or GIF.')
  }
  if (file.size > IMAGE_MAX_BYTES) {
    throw new Error('That picture is larger than 5 MB. An email is read on a phone.')
  }

  const original = await put(await shrink(file, ''), file.type)
  if (!ratio) return { src: original, original }
  const src = await put(await shrink(file, ratio), file.type)
  return { src, original }
}

/**
 * Crop a picture that is already uploaded, into a new one.
 *
 * Reads the original back out of the bucket, which works because the bucket
 * allows GET from our own origins — see the CORS configuration. Without it the
 * canvas would be tainted and a ratio could only ever be chosen once, at upload.
 *
 * The result is a new object rather than a replacement: the original stays put
 * so the ratio can be changed again, and an email already sent keeps pointing
 * at the file it was sent with.
 */
export async function recropEmailImage(originalUrl: string, ratio: string): Promise<string> {
  const res = await fetch(originalUrl)
  if (!res.ok) throw new Error('The original could not be read back. Upload it again.')
  const blob = await res.blob()
  if (!ALLOWED.includes(blob.type)) throw new Error('That file is not an image any more.')
  const cropped = await shrink(blob, ratio)
  return put(cropped, blob.type)
}
